import React, { useState } from 'react';
import { permissionObj } from '../../../../Helpers/permission';
import { statusBorderColor, statusColor, statusFontColor } from '../../../../enums/statusColorEnum';
import { status, STATUS_MODEL } from '../../../../enums/statusEnum';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CustomTh from '../../../../components/table/CustomTh';
import CustomTd from '../../../../components/table/CustomTd';
import DropDownMenuProfile from '../../../../components/DropDown/DropDownMenuProfile';
import DropDownMenuItem from '../../../../components/DropDown/DropDownMenuItem';
import TableFunction from '../../../../components/table/TableFunction';
import CustomChip from '../../../../components/CustomChip';
import {
    DATE_TIME_FORMAT_NAME_MODEL,
    EMPTY_VALUES,
    errorMsg,
    getDateFormat,
    getFullName,
    getTimeFormat,
} from '../../../../Apis/functions';
import { MODELS } from '../../../../Apis/Models';
import { TABLES } from '../../../../Apis/Tables';
import { SimpleSearch, SimpleSearchWithId } from '../../../../reduxToolkit/Search/SearchApi';
import { DeleteItemsApi } from '../../../../reduxToolkit/Commons/CommonsApi';
import routes from '../../../../Routes/routes';
import {
    CancelIconsOptions,
    DeleteIconsOptions,
    DeliveryBoxIcon,
    DetailsIconsOptions,
    IconClockTable,
    IconDocumentCheck,
    IconPlusFill,
    IconScheduleTable,
    PersonOutlinedChip,
    UpdateIconsOptions,
} from '../../../../components/buttons/IconButtons';
import {
    SEARCH_MODEL,
    DEFAULT_LEFT,
    LEFT_FIRST_COLUMN_WITH_CHECKBOX,
} from '../../../../components/table/Model/TableModel';
import CustomTooltip from '../../../../components/CustomTooltip';
import { CancelSupplierOrder } from '../../../../reduxToolkit/Providers/providersApi';
import { toast } from 'react-toastify';
import ListDisplayWithTooltip from "../../../../components/ListDisplayWithTooltip";

/**
 * @email azapata@ibl.mx
 * @author Ferro
 * @jobTitle Full Stack Developer
 */

/**
 * Initializes and returns an object for managing and displaying orders in a table view.
 *
 * @param {string} search
 *   Current simple search text.
 * @param {string} supplierId
 *   Identifier of the selected supplier to filter orders by.
 * @param {string} searchElement
 *   Currently selected advanced search field name.
 * @param {string} contractorId
 *   Identifier of the selected contractor to filter orders by.
 * @param {function(Object[]): void} setCriteria
 *   Setter for the list of applied advanced search criteria.
 *   Setter for the simple search text.
 * @param {Array<any>} finalArray
 *   Array of orders after applying all filters and searches.
 *   Setter to indicate whether the data has finished loading.
 * @param {function(boolean): void} setModified
 *   Setter to mark that the orders list has been modified.
 * @param {boolean} modified
 *   Flag indicating whether the orders list has been modified since last load.
 *   Current active step in a multi-step workflow (e.g., advanced search steps).
 *   Setter for the currently selected advanced search field.
 *   Clears all advanced search criteria.
 *   Setter for the list of order IDs selected for deletion.
 * @param {function(Array<any>): void} setBodyForExport
 *   Setter for the payload array to be exported (e.g., CSV or Excel).
 *   Setter for the detailed criteria list for display or export.
 */

export const PurchaseOrderTabApp = ({
    supplierId,
    searchElement,
    setModified,
    modified,
    setBodyForExport,
    selectedList,
    setSelectedList,
    setIsAllChecked,
    canceledList,
    setCanceledList,
    setDeleteOrderShow,
}) => {
    const dispatch = useDispatch();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const { t } = useTranslation();
    const tableId = 'tbl_pot_purchase_orders';
    const searchId = 'txt_pot_purchase_orders';
    const moduleId = `${MODELS.Order}`;
    const option = `${TABLES.ORDERS}`;
    const [data, setData] = useState({ content: [], totalElements: 0 });
    const [showDeliveryModal, setShowDeliveryModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [itemSelected, setItemSelected] = useState(null);
    const deletePermission =
        (DEV || permission?.includes(permissionObj?.WEB_SUPPLIER_ORDER_DELETE)) && supplierId === null;
    const leftPosition = deletePermission ? LEFT_FIRST_COLUMN_WITH_CHECKBOX : DEFAULT_LEFT;

    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes='first_head'>
                {t('code')}
            </CustomTh>,
            <CustomTh tableId={tableId}>{t('item')}</CustomTh>,
            supplierId === null && <CustomTh tableId={tableId}>{t('supplier')}</CustomTh>,
            supplierId === null && <CustomTh tableId={tableId}>{t('company')}</CustomTh>,

            <CustomTh tableId={tableId}>{t('supplier_employee')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('supplier_vehicle')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('received_by')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('delivered_date')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('status')}</CustomTh>,
        ];
    };

    const getTbody = () => {
        // Covers null/undefined content so the empty state always receives an empty list
        if (!data?.content?.length) return { body: [], data: [] };

        let body = data?.content?.map((item) => {
            const companyFullName = item.supplierCompanyAcronym
                ? `${item.supplierCompanyAcronym} | ${item.supplierCompanyName || ''}`
                : item.supplierCompanyName || EMPTY_VALUES.DEFAULT;

            const users = item.receivers || [];

            return (
                <>
                    <CustomTd
                        tableId={tableId}
                        tooltip={item?.folio}
                        fixed={true}
                        left={leftPosition}
                        classes={'first-column'}
                    >
                        {item?.folio || EMPTY_VALUES.DEFAULT}
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomTooltip
                            text={item?.item || EMPTY_VALUES.DEFAULT}
                            classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis`}
                            showIfTruncated={true}
                        />
                    </CustomTd>
                    {supplierId === null && (
                        <CustomTd tableId={tableId}>
                            <CustomTooltip
                                text={getFullName(
                                    item.supplierName,
                                    item.supplierLastName,
                                    item.supplierSecondLastName,
                                )}
                                classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis`}
                                showIfTruncated={true}
                            />
                        </CustomTd>
                    )}
                    {supplierId === null && (
                        <CustomTd tableId={tableId}>
                            <CustomTooltip
                                text={companyFullName}
                                classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis`}
                                showIfTruncated={true}
                            />
                        </CustomTd>
                    )}

                    <CustomTd tableId={tableId}>
                        <CustomTooltip
                            text={
                                item.supplierEmployeeId
                                    ? getFullName(
                                          item.supplierEmployeeName,
                                          item.supplierEmployeeLastName,
                                          item.supplierEmployeeSecondLastName,
                                      )
                                    : EMPTY_VALUES.DEFAULT
                            }
                            classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis`}
                            showIfTruncated={true}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        {item.supplierVehicleId
                            ? getFullName(item.supplierVehicleBrand, '|', item.supplierVehicleSubBrand)
                            : EMPTY_VALUES.DEFAULT}
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <ListDisplayWithTooltip
                            items={users}
                            renderItem={(u) => u.name}
                            maxInline={2}
                            emptyValue={EMPTY_VALUES.DEFAULT}
                            icon={<IconPlusFill width={20} height={20} />}
                            itemIcon={<PersonOutlinedChip className="icon-color-general-950" />}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <div className='d-flex align-items-center'>
                            <div>
                                <IconScheduleTable className='mr-8px mb-1 icon-color-general-950' />
                            </div>
                            {item?.actualDeliveryDate
                                ? getDateFormat(item?.actualDeliveryDate, false, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)
                                : EMPTY_VALUES.DEFAULT}
                        </div>

                        <div className='d-flex align-items-center'>
                            <div>
                                <IconClockTable className='mr-8px mb-1 icon-color-general-950' />
                            </div>
                            {item?.actualDeliveryDate ? getTimeFormat(item?.actualDeliveryDate) : EMPTY_VALUES.DEFAULT}
                        </div>
                    </CustomTd>

                    <CustomTd tableId={tableId}>
                        <CustomChip
                            fontColor={statusFontColor[item?.statusId]}
                            borderColor={statusBorderColor[item?.statusId]}
                            background={statusColor[item?.statusId]}
                            legend={t(status[item?.statusId]) || EMPTY_VALUES.DEFAULT}
                            variant={'large'}
                        />
                    </CustomTd>
                </>
            );
        });
        return {
            body: body,
            data: data?.content,
        };
    };

    const getEmployeeActions = (item, index) => {
        if (!DEV && !permission?.includes(permissionObj?.WEB_SUPPLIER_ORDER_READ)) {
            return EMPTY_VALUES.DEFAULT;
        }

        return (
            <DropDownMenuProfile openMenu={openMenu} handleMenuToggle={handleMenuToggle} menuId={`menu-${item.id}`}>
                <>
                    <DropDownMenuItem
                        index={index}
                        handleClose={handleClose}
                        name={t('details')}
                        url={`${routes.employee.orderDetails}${item?.id}`}
                        icon={<DetailsIconsOptions />}
                    />

                    {item.statusId === STATUS_MODEL.ORDER_INCOMING && (
                        <>
                         {(DEV || permission?.includes(permissionObj?.WEB_SUPPLIER_ORDER_UPDATE)) && (
                            <DropDownMenuItem
                                index={index}
                                handleClose={handleClose}
                                name={t('update')}
                                url={`${routes.employee.updateOrder}${item?.id}`}
                                icon={<UpdateIconsOptions />}
                            />
                              )}
                            {(DEV || permission?.includes(permissionObj?.WEB_SUPPLIER_ORDER_UPDATE)) && (
                            <DropDownMenuItem
                                index={index}
                                handleClose={handleClose}
                                name={t('cancel')}
                                click={() => {
                                    setCanceledList([item?.id]);
                                    setShowCancelModal(true);
                                }}
                                fontColor={'font-color-error-600'}
                                icon={<CancelIconsOptions />}
                                iconColor={'icon-color-error-code-600'}
                            />
                            )}
                        </>
                    )}

                    {[STATUS_MODEL.ORDER_INCOMING, STATUS_MODEL.ORDER_STARTED].includes(item.statusId) && (
                        <>
                         {(DEV || permission?.includes(permissionObj?.WEB_SUPPLIER_ORDER_DELETE)) && (
                            <DropDownMenuItem
                                index={index}
                                handleClose={handleClose}
                                name={t('delete')}
                                fontColor='font-color-error-600'
                                iconColor={'icon-color-error-code-600'}
                                click={() => {
                                    setSelectedList([item.id]);
                                    setDeleteOrderShow(true);
                                }}
                                icon={<DeleteIconsOptions />}
                                isDelete={true}
                            />
                        )}
                        </>
                    )}
                </>
            </DropDownMenuProfile>
        );
    };

    const getSupplierActions = (item, index) => {
        return (
            <DropDownMenuProfile openMenu={openMenu} handleMenuToggle={handleMenuToggle} menuId={`menu-${item.id}`}>
                {item.statusId === STATUS_MODEL.ORDER_INCOMING && (
                    <>
                        <DropDownMenuItem
                            index={index}
                            handleClose={handleClose}
                            name={t('complete')}
                            url={`${routes.suppliers.completeOrder}${item?.id}`}
                            icon={<IconDocumentCheck />}
                        />
                    </>
                )}

                <DropDownMenuItem
                    index={index}
                    handleClose={handleClose}
                    name={t('details')}
                    url={`${routes.suppliers.ordersDetail}${item?.id}`}
                    icon={<DetailsIconsOptions />}
                />

                {item.statusId === STATUS_MODEL.ORDER_STARTED && (
                    <>
                        <DropDownMenuItem
                            index={index}
                            handleClose={handleClose}
                            name={t('update')}
                            url={`${routes.suppliers.updateOrder}${item?.id}`}
                            icon={<UpdateIconsOptions />}
                        />
                        <DropDownMenuItem
                            index={index}
                            handleClose={handleClose}
                            name={t('deliver')}
                            click={() => {
                                setItemSelected(item);
                                setShowDeliveryModal(true);
                            }}
                            icon={<DeliveryBoxIcon />}
                        />
                    </>
                )}
            </DropDownMenuProfile>
        );
    };

    const getActionsButtons = (item, index) => {
        if (!supplierId) {
            return getEmployeeActions(item, index);
        }

        return getSupplierActions(item, index);
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body =
            supplierId === null
                ? SEARCH_MODEL.setObject({ page, size: rowsPerPage, model: option, key: searchElement })
                : SEARCH_MODEL.setObject({
                      page,
                      size: rowsPerPage,
                      model: option,
                      key: searchElement,
                      userId: supplierId,
                  });

        setBodyForExport(body);
        const dispatchFunction = supplierId === null ? SimpleSearch : SimpleSearchWithId;
        dispatch(dispatchFunction({ body })).then(
            ({
                payload: {
                    data: { data, success },
                },
            }) => {
                if (success === true) {
                    setData(data);
                }
            },
        );
    };

    const {
        thead,
        tbody,
        handleCheckboxChange,
        handleDeleteAll,
        openMenu,
        handleMenuToggle,
        handleClose,
        pagination,
        page,
        rowsPerPage,
    } = TableFunction({
        selectedList, // Current selection, needed to keep the select all checkbox in sync
        setSelectedList,
        data,
        getThead,
        getTbody,
        labelRowsPerPage: 'purchase_orders_per_page',
        fixedPagination: false,
        hasContainer: true,
        setIsAllChecked,
        searchElement, //New
        getData, //New
        modified, //New
        setModified, //New
    });

    const deleteSelectedOrders = (deleteItem) => {
        const tableName = TABLES.PURCHASE;
        const body = deleteItem;

        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName, body })).then(
                ({
                    payload: {
                        data: { success, code },
                    },
                }) => {
                    if (success === true) {
                        setSelectedList([]);
                        setIsAllChecked(false);
                        setModified(true); // Mark the data as modified
                    } else errorMsg(code);
                },
            );
        }
    };

    const cancelOrder = () => {
        if (canceledList.length === 0) return;
        dispatch(CancelSupplierOrder(canceledList[0])).then(
            ({
                payload: {
                    data: { success, code },
                },
            }) => {
                setShowCancelModal(false);
                setCanceledList([]);

                if (success === true) {
                    toast.success(t('has_been_cancelled_successfully'));
                    setModified(true); // Mark the data as modified
                } else errorMsg(code);
            },
        );
    };

    return {
        t,
        DEV,
        permission,
        tableId,
        searchId,
        thead,
        tbody,
        page,
        rowsPerPage,
        moduleId,
        option,
        setData,
        deletePermission,
        handleDeleteAll,
        deleteSelectedOrders,
        getActionsButtons,
        pagination,
        handleCheckboxChange,
        showDeliveryModal,
        setShowDeliveryModal,
        showCancelModal,
        setShowCancelModal,
        cancelOrder,
        itemSelected,
    };
};
